import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Loader2, AlertCircle, FileText, Bot, MessageSquare, Database, Target } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '@/config/api';

interface ProgressUpdate {
  step: string;
  message: string;
  progress?: number;
  count?: number;
  original?: number;
  deduped?: number;
  totalQuestions?: number;
  sourceCounts?: Record<string, number>;
  wheelId?: string;
  error?: string;
  // Detailed progress fields for SERP analysis
  analyzed?: number;
  total?: number;
  batches_processed?: number;
  total_batches?: number;
}

interface GenerationSummary {
  totalQuestions: number;
  sourceCounts: Record<string, number>;
  centerTerm: string;
  generatedAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onComplete?: (wheelId: string, summary: GenerationSummary) => void;
  progressId: string | null;
  centerTerm: string;
}

const stepLabels: Record<string, { label: string; icon: React.ReactNode }> = {
  start: { label: 'Starting', icon: <Loader2 className="w-4 h-4 animate-spin" /> },
  templates: { label: 'Templates', icon: <FileText className="w-4 h-4" /> },
  grammar: { label: 'Grammar Check', icon: <FileText className="w-4 h-4" /> },
  ai: { label: 'AI Generation', icon: <Bot className="w-4 h-4" /> },
  reviews: { label: 'Customer Reviews', icon: <MessageSquare className="w-4 h-4" /> },
  google_ads: { label: 'Google Ads', icon: <Database className="w-4 h-4" /> },
  dedup: { label: 'Deduplication', icon: <Target className="w-4 h-4" /> },
  enrichment: { label: 'Search Data', icon: <Database className="w-4 h-4" /> },
  paa_extraction: { label: 'People Also Ask', icon: <MessageSquare className="w-4 h-4" /> },
  gaps: { label: 'Content Gaps', icon: <Target className="w-4 h-4" /> },
  scoring: { label: 'Opportunity Scoring', icon: <Target className="w-4 h-4" /> },
  structure: { label: 'Structuring Data', icon: <Database className="w-4 h-4" /> },
  save: { label: 'Saving', icon: <Database className="w-4 h-4" /> },
  complete: { label: 'Complete', icon: <CheckCircle2 className="w-4 h-4 text-green-500" /> },
  error: { label: 'Error', icon: <AlertCircle className="w-4 h-4 text-red-500" /> }
};

// Approximate progress percentages for each step
const stepProgress: Record<string, number> = {
  initializing: 2,
  language_detected: 5,
  sector_analysis: 10,
  personas_enrichment: 15,
  personas_loaded: 20,
  brands_research: 25,
  brands_mapping: 30,
  categories_research: 35,
  questions_generation: 40,
  questions_genai_personas: 45,
  questions_google_ads: 50,
  questions_categories: 55,
  questions_comparisons: 60,
  questions_characteristics: 65,
  questions_reviews: 70,
  questions_tech_reviews: 72,
  questions_generated: 75,
  questions_enrichment: 78,
  paa_extraction: 80,
  grammar_check: 85,
  deduplication: 88,
  paa_recursive: 90,
  final_dedup: 92,
  clustering: 95,
  storing: 98,
  completed: 100,
  error: 0
};

export function QuestionGenerationProgressModal({ open, onClose, onComplete, progressId, centerTerm }: Props) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [currentStep, setCurrentStep] = useState<string>('start');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('Initializing...');
  const [stepHistory, setStepHistory] = useState<ProgressUpdate[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<GenerationSummary | null>(null);
  const [detailedProgress, setDetailedProgress] = useState<{
    analyzed?: number;
    total?: number;
    batches_processed?: number;
    total_batches?: number;
  }>({});

  useEffect(() => {
    if (!open || !progressId) return;

    // Connect to Socket.IO with polling-first transport for reliability
    // WebSocket upgrade often fails in Docker/proxy environments
    const socketInstance = io(SOCKET_URL, {
      transports: ['polling', 'websocket'], // Polling first, then upgrade to WebSocket
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 10000
    });

    socketInstance.on('connect', () => {
      console.log('[Progress] Socket connected via', socketInstance.io.engine.transport.name);
      // Join the progress room
      socketInstance.emit('join-room', `question-wheel-progress:${progressId}`);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('[Progress] Connection error:', error.message);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('[Progress] Disconnected:', reason);
    });

    socketInstance.on('question-wheel-progress', (data: ProgressUpdate) => {
      console.log('[Progress] Update received:', data);

      setCurrentStep(data.step);
      setMessage(data.message);

      // Handle progress percentage
      if (data.progress !== undefined) {
        // Use explicit progress if provided
        setProgress(data.progress);
      } else if (stepProgress[data.step] !== undefined) {
        // Use step-based progress mapping
        setProgress(stepProgress[data.step]);
      }

      // Update detailed progress if available
      if (data.analyzed !== undefined || data.total !== undefined) {
        setDetailedProgress({
          analyzed: data.analyzed,
          total: data.total,
          batches_processed: data.batches_processed,
          total_batches: data.total_batches
        });
      }

      // Add to history
      setStepHistory(prev => [...prev, data]);

      // Handle completion
      if (data.step === 'complete') {
        setIsComplete(true);
        if (data.sourceCounts && data.totalQuestions) {
          const summaryData: GenerationSummary = {
            totalQuestions: data.totalQuestions,
            sourceCounts: data.sourceCounts,
            centerTerm,
            generatedAt: new Date().toISOString()
          };
          setSummary(summaryData);

          if (onComplete && data.wheelId) {
            onComplete(data.wheelId, summaryData);
          }
        }
      }

      // Handle errors
      if (data.step === 'error') {
        setError(data.error || 'Unknown error occurred');
      }
    });

    setSocket(socketInstance);

    return () => {
      console.log('[Progress] Cleaning up socket connection');
      socketInstance.disconnect();
    };
  }, [open, progressId, centerTerm, onComplete]);

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'template':
        return <FileText className="w-4 h-4" />;
      case 'ai':
        return <Bot className="w-4 h-4" />;
      case 'google_paa':
        return <Database className="w-4 h-4" />;
      case 'reviews':
        return <MessageSquare className="w-4 h-4" />;
      case 'google_ads':
        return <Database className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      template: 'Templates',
      ai: 'AI Generated',
      google_paa: 'Google PAA',
      reviews: 'Customer Reviews',
      google_ads: 'Google Ads',
      multi_source: 'Multi-Source'
    };
    return labels[source] || source;
  };

  return (
    <Dialog open={open} onOpenChange={() => !isComplete && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {error ? (
              <>
                <AlertCircle className="w-5 h-5 text-red-500" />
                Generation Failed
              </>
            ) : isComplete ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Generation Complete!
              </>
            ) : (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating Question Wheel
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {error ? (
              `Error: ${error}`
            ) : isComplete ? (
              `Successfully generated questions for "${centerTerm}"`
            ) : (
              `Generating questions for "${centerTerm}"...`
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Bar */}
          {!error && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{message}</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />

              {/* Detailed Progress - Show question counts when available */}
              {detailedProgress.analyzed !== undefined && detailedProgress.total !== undefined && (
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">
                      {detailedProgress.analyzed} / {detailedProgress.total} questions
                    </span>
                    {detailedProgress.batches_processed !== undefined && detailedProgress.total_batches !== undefined && (
                      <span>
                        (Batch {detailedProgress.batches_processed} / {detailedProgress.total_batches})
                      </span>
                    )}
                  </div>
                  {detailedProgress.analyzed && detailedProgress.total && detailedProgress.batches_processed && detailedProgress.total_batches && (
                    <span>
                      ~{Math.ceil((detailedProgress.total - detailedProgress.analyzed) /
                        Math.max(1, detailedProgress.analyzed / detailedProgress.batches_processed) / 60)} min remaining
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Current Step */}
          {!error && !isComplete && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  {stepLabels[currentStep]?.icon}
                  {stepLabels[currentStep]?.label || currentStep}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{message}</p>
              </CardContent>
            </Card>
          )}

          {/* Summary View (when complete) */}
          {isComplete && summary && (
            <Card>
              <CardHeader>
                <CardTitle>Generation Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-2xl font-bold">{summary.totalQuestions}</p>
                  <p className="text-sm text-muted-foreground">Total Questions Generated</p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Questions by Source</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(summary.sourceCounts)
                      .filter(([_, count]) => count > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([source, count]) => (
                        <div key={source} className="flex items-center justify-between p-2 border rounded">
                          <div className="flex items-center gap-2">
                            {getSourceIcon(source)}
                            <span className="text-sm">{getSourceLabel(source)}</span>
                          </div>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <button
                    onClick={onClose}
                    className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    View Question Wheel
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {error && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Generation Failed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={onClose}
                  className="mt-4 w-full px-4 py-2 border border-red-300 rounded-md hover:bg-red-100 transition-colors"
                >
                  Close
                </button>
              </CardContent>
            </Card>
          )}

          {/* Step History (collapsible) */}
          {stepHistory.length > 0 && !isComplete && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
                View Progress History ({stepHistory.length} steps)
              </summary>
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {stepHistory.map((step, index) => (
                  <div key={index} className="flex items-start gap-2 p-2 hover:bg-accent/50 rounded">
                    {stepLabels[step.step]?.icon}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{stepLabels[step.step]?.label || step.step}</p>
                      <p className="text-xs text-muted-foreground truncate">{step.message}</p>
                    </div>
                    {step.progress !== undefined && (
                      <span className="text-xs text-muted-foreground">{step.progress}%</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
